import { createContext, useContext, useState, useRef, useEffect } from "react";
import api from "../utils/api";

const AuthContext = createContext();

// ── Persist helpers ──────────────────────────────────────
function loadOrg() {
  try { return JSON.parse(localStorage.getItem("trackin_org") || "null"); }
  catch { return null; }
}

// Strip tracking state before syncing to backend
function metadataOnly(vehicles) {
  return vehicles.map(v => ({
    id:            v.id,
    plateNumber:   v.plateNumber,
    nickname:      v.nickname,
    type:          v.type,
    ownerName:     v.ownerName || null,
    contactNumber: v.contactNumber || null,
    fastagId:      v.fastagId || null,
  }));
}

// ── Provider ─────────────────────────────────────────────
export function AuthProvider({ children }) {
  const [org,        setOrgState]  = useState(() => loadOrg());
  const [quotaSynced, setQuotaSynced] = useState(false);
  const orgRef                     = useRef(org);
  const syncTimer                  = useRef(null);

  function setOrg(next) {
    const value = typeof next === "function" ? next(orgRef.current) : next;
    orgRef.current = value;
    setOrgState(value);
    if (value) localStorage.setItem("trackin_org", JSON.stringify(value));
    else        localStorage.removeItem("trackin_org");
  }

  // session shape expected by pages/OrgApp
  const session    = org ? { orgId: org.id, email: org.email, name: org.name } : null;
  const currentOrg = org;
  const ownerCode  = process.env.REACT_APP_OWNER_CODE;
  const BYPASS     = process.env.REACT_APP_BYPASS_QUOTA === "true"
                  || (!!ownerCode && localStorage.getItem("trackin_owner_bypass") === ownerCode);
  const isSuspended  = !BYPASS && (org?.isSuspended === true);
  const isPaid       = BYPASS || (!isSuspended && !!org && org.hasPaid === true);
  const apiCallsLeft = BYPASS
    ? Infinity
    : org ? Math.max(0, (org.apiCallsTotal || 0) - (org.apiCallsUsed || 0)) : 0;
  // Only show exhausted wall AFTER a DB sync confirms the quota — prevents stale localStorage from
  // triggering the wall on page load before the server is consulted.
  const isExhausted  = !BYPASS && quotaSynced && isPaid && apiCallsLeft <= 0;
  const isFleet      = org?.productTier === "fleet";

  // ── Quota sync helper ─────────────────────────────────────────────────────────
  function syncQuotaFromDB() {
    const token = localStorage.getItem("trackin_token");
    if (!token) return;
    api.get("/api/v/quota").then(({ data }) => {
      setQuotaSynced(true);
      setOrg(prev => prev ? {
        ...prev,
        apiCallsUsed:  data.apiCallsUsed,
        apiCallsTotal: data.apiCallsTotal,
        hasPaid:       data.hasPaid,
        isSuspended:   data.isSuspended,
        canTopup:      data.canTopup ?? prev.canTopup ?? false,
        productTier:   data.productTier ?? prev.productTier ?? "tracking",
      } : prev);
    }).catch(() => { setQuotaSynced(true); });
  }

  // On mount: sync quota from DB (fixes stale local count after re-login)
  useEffect(() => {
    if (org) syncQuotaFromDB();
  }, []); // eslint-disable-line

  // Listen for 402/403 events fired by the axios interceptor — sync immediately
  useEffect(() => {
    window.addEventListener("trackin:quota-sync", syncQuotaFromDB);
    return () => window.removeEventListener("trackin:quota-sync", syncQuotaFromDB);
  }, []); // eslint-disable-line

  // Re-sync quota every 20s so admin-side grants/deductions/tier changes
  // reflect in an open org session almost immediately
  useEffect(() => {
    if (!org) return;
    const id = setInterval(syncQuotaFromDB, 20 * 1000);
    return () => clearInterval(id);
  }, [!!org]); // eslint-disable-line

  // ── Auth ───────────────────────────────────────────────
  async function register(name, email, password, couponCode = "", phone = "", gstNumber = "", panNumber = "", billingAddress = "", productTier = "tracking") {
    const { data } = await api.post("/api/v/create", {
      name, email, password, couponCode, phone, gstNumber, panNumber, billingAddress, productTier,
    });
    localStorage.setItem("trackin_token", data.token);
    setOrg(data.org);
    setQuotaSynced(true);
    return data.couponApplied;
  }

  async function login(email, password) {
    const { data } = await api.post("/api/v/connect", { email, password });
    localStorage.setItem("trackin_token", data.token);
    setOrg(data.org);
    setQuotaSynced(true);
  }

  function logout() {
    localStorage.removeItem("trackin_token");
    setOrg(null);
    setQuotaSynced(false);
  }

  // Apply server-confirmed quota update from PhonePe status response
  function applyPaymentResult(data) {
    setOrg(prev => prev ? {
      ...prev,
      hasPaid:       data.hasPaid,
      apiCallsUsed:  data.apiCallsUsed,
      apiCallsTotal: data.apiCallsTotal,
      // Present on a fleet_upgrade purchase — flips isFleet immediately, no re-login needed
      productTier:   data.productTier ?? prev.productTier,
    } : prev);
    setQuotaSynced(true);
  }

  // Saves GST/PAN/billing address for an org that paid before this field existed
  // at signup — the backend re-sends that org's existing invoice(s) with the
  // corrected details once saved.
  async function updateBillingDetails({ gstNumber, panNumber, billingAddress }) {
    const { data } = await api.patch("/api/v/account/billing", { gstNumber, panNumber, billingAddress });
    setOrg(prev => prev ? {
      ...prev,
      gstNumber:      data.gstNumber,
      panNumber:      data.panNumber,
      billingAddress: data.billingAddress,
    } : prev);
    return data;
  }

  // Optimistic local decrement — backend enforces the real quota on /api/fastag
  function consumeApiCall() {
    if (BYPASS) return true;
    if (!org || !org.hasPaid) return false;
    if (org.apiCallsUsed >= org.apiCallsTotal) return false;
    setOrg(prev => prev ? { ...prev, apiCallsUsed: (prev.apiCallsUsed || 0) + 1 } : prev);
    return true;
  }

  // Debounced sync to backend — only metadata, not tracking state
  // Also strip tracking state from localStorage so stale isTracking/status never persists
  function saveOrgVehicles(vehicles) {
    const forStorage = metadataOnly(vehicles);
    setOrg(prev => prev ? { ...prev, vehicles: forStorage } : prev);

    if (syncTimer.current) clearTimeout(syncTimer.current);
    syncTimer.current = setTimeout(() => {
      api.put("/api/vehicles", forStorage).catch(err => {
        console.warn("Vehicle sync failed:", err.message);
      });
    }, 2000);
  }

  const canTopup = org?.canTopup === true;

  return (
    <AuthContext.Provider value={{
      session, currentOrg, orgs: org ? [org] : [],
      register, login, logout,
      applyPaymentResult, consumeApiCall, saveOrgVehicles, syncQuotaFromDB, updateBillingDetails,
      isPaid, apiCallsLeft, isExhausted, isSuspended, canTopup, isFleet,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
